import { Abstract } from '@/module/Author/Abstract';
import { Article } from '@/module/Author/Article';
import { Award } from '@/module/Author/Award';
import { Book } from '@/module/Author/Book';
import { Chapter } from '@/module/Author/Chapter';
import { Committee } from '@/module/Author/Committee';
import { Course } from '@/module/Author/Course';
import { Dictionary } from '@/module/Author/Dictionary';
import { DoctoralCommittee } from '@/module/Author/DoctoralCommittee';
import { EditorialTeam } from '@/module/Author/EditorialTeam';
import { Patent } from '@/module/Author/Patent';
import { Presentation } from '@/module/Author/Presentation';
import { Proceeding } from '@/module/Author/Proceeding';
import { Project } from '@/module/Author/Project';
import { Translation } from '@/module/Author/Translation';

import { PersistentFile } from '@feature/utils';

export type EvalTemplateCriteriaType =
  | 'FREE_ANSWER'
  | 'NUMBER'
  | 'BOOLEAN'
  | 'SELECT_ABSTRACT'
  | 'SELECT_ARTICLE'
  | 'SELECT_BOOK'
  | 'SELECT_CHAPTER'
  | 'SELECT_COURSE'
  | 'SELECT_COMMITTEE'
  | 'SELECT_PROJECT'
  | 'SELECT_PATENT'
  | 'SELECT_TRANSLATION'
  | 'SELECT_PROCEEDING'
  | 'SELECT_AWARD'
  | 'SELECT_EDITORIAL_TEAM'
  | 'SELECT_DOCTORAL_COMMITTEE'
  | 'SELECT_PRESENTATION'
  | 'SELECT_DICTIONARY';

interface BaseEvaluationItem {
  id: number;
  note: string;
  link: string | null;
  file: PersistentFile;

  criteriaType: EvalTemplateCriteriaType;
}

export type EvaluationItem =
  | EvalCriteriaFreeItem
  | EvalCriteriaBooleanItem
  | EvalCriteriaNumberItem
  | EvalCriteriaCourseItem
  | EvalCriteriaAbstractItem
  | EvalCriteriaArticleItem
  | EvalCriteriaBookItem
  | EvalCriteriaChapterItem
  | EvalCriteriaCommitteeItem
  | EvalCriteriaPatentItem
  | EvalCriteriaProjectItem
  | EvalCriteriaTranslationItem
  | EvalCriteriaProceedingItem
  | EvalCriteriaDictionaryItem
  | EvalCriteriaPresentationItem
  | EvalCriteriaDoctoralCommitteeItem
  | EvalCriteriaEditorialTeamItem
  | EvalCriteriaAwardItem;

export function isEvalCriteriaFreeItem(item: EvaluationItem): item is EvalCriteriaFreeItem {
  return item.criteriaType === 'FREE_ANSWER';
}

export function isEvalCriteriaBooleanItem(item: EvaluationItem): item is EvalCriteriaBooleanItem {
  return item.criteriaType === 'BOOLEAN';
}

export function isEvalCriteriaNumberItem(item: EvaluationItem): item is EvalCriteriaNumberItem {
  return item.criteriaType === 'NUMBER';
}

export function isEvalCriteriaAbstractItem(item: EvaluationItem): item is EvalCriteriaAbstractItem {
  return item.criteriaType === 'SELECT_ABSTRACT';
}

export function isEvalCriteriaArticleItem(item: EvaluationItem): item is EvalCriteriaArticleItem {
  return item.criteriaType === 'SELECT_ARTICLE';
}

export function isEvalCriteriaBookItem(item: EvaluationItem): item is EvalCriteriaBookItem {
  return item.criteriaType === 'SELECT_BOOK';
}

export function isEvalCriteriaTranslationItem(item: EvaluationItem): item is EvalCriteriaTranslationItem {
  return item.criteriaType === 'SELECT_TRANSLATION';
}

export function isEvalCriteriaChapterItem(item: EvaluationItem): item is EvalCriteriaChapterItem {
  return item.criteriaType === 'SELECT_CHAPTER';
}

export function isEvalCriteriaCommitteeItem(item: EvaluationItem): item is EvalCriteriaCommitteeItem {
  return item.criteriaType === 'SELECT_COMMITTEE';
}

export function isEvalCriteriaCourseItem(item: EvaluationItem): item is EvalCriteriaCourseItem {
  return item.criteriaType === 'SELECT_COURSE';
}

export function isEvalCriteriaProjectItem(item: EvaluationItem): item is EvalCriteriaProjectItem {
  return item.criteriaType === 'SELECT_PROJECT';
}

export function isEvalCriteriaPatentItem(item: EvaluationItem): item is EvalCriteriaPatentItem {
  return item.criteriaType === 'SELECT_PATENT';
}

export function isEvalCriteriaAwardItem(item: EvaluationItem): item is EvalCriteriaAwardItem {
  return item.criteriaType === 'SELECT_AWARD';
}

export function isEvalCriteriaPresentationItem(item: EvaluationItem): item is EvalCriteriaPresentationItem {
  return item.criteriaType === 'SELECT_PRESENTATION';
}

export function isEvalCriteriaDictionaryItem(item: EvaluationItem): item is EvalCriteriaDictionaryItem {
  return item.criteriaType === 'SELECT_DICTIONARY';
}

export function isEvalCriteriaDoctoralCommitteeItem(item: EvaluationItem): item is EvalCriteriaDoctoralCommitteeItem {
  return item.criteriaType === 'SELECT_DOCTORAL_COMMITTEE';
}

export function isEvalCriteriaEditorialTeamItem(item: EvaluationItem): item is EvalCriteriaEditorialTeamItem {
  return item.criteriaType === 'SELECT_EDITORIAL_TEAM';
}

export function isEvalCriteriaProceedingItem(item: EvaluationItem): item is EvalCriteriaProceedingItem {
  return item.criteriaType === 'SELECT_PROCEEDING';
}

export interface EvalCriteriaFreeItem extends BaseEvaluationItem {
  criteriaType: 'FREE_ANSWER';
  activityDescription: string;
}

export interface EvalCriteriaBooleanItem extends BaseEvaluationItem {
  criteriaType: 'BOOLEAN';
}

export interface EvalCriteriaNumberItem extends BaseEvaluationItem {
  criteriaType: 'NUMBER';
  number: number;
}

export interface EvalCriteriaAbstractItem extends BaseEvaluationItem {
  criteriaType: 'SELECT_ABSTRACT';
  abstractItem: Abstract;
}

export interface EvalCriteriaArticleItem extends BaseEvaluationItem {
  criteriaType: 'SELECT_ARTICLE';
  article: Article;
}

export interface EvalCriteriaBookItem extends BaseEvaluationItem {
  criteriaType: 'SELECT_BOOK';
  book: Book;
}

export interface EvalCriteriaChapterItem extends BaseEvaluationItem {
  criteriaType: 'SELECT_CHAPTER';
  chapter: Chapter;
}

export interface EvalCriteriaDictionaryItem extends BaseEvaluationItem {
  criteriaType: 'SELECT_DICTIONARY';
  dictionary: Dictionary;
}

export interface EvalCriteriaAwardItem extends BaseEvaluationItem {
  criteriaType: 'SELECT_AWARD';
  award: Award;
}

export interface EvalCriteriaDoctoralCommitteeItem extends BaseEvaluationItem {
  criteriaType: 'SELECT_DOCTORAL_COMMITTEE';
  doctoralCommittee: DoctoralCommittee;
}

export interface EvalCriteriaEditorialTeamItem extends BaseEvaluationItem {
  criteriaType: 'SELECT_EDITORIAL_TEAM';
  editorialTeam: EditorialTeam;
}

export interface EvalCriteriaPresentationItem extends BaseEvaluationItem {
  criteriaType: 'SELECT_PRESENTATION';
  presentation: Presentation;
}

export interface EvalCriteriaProceedingItem extends BaseEvaluationItem {
  criteriaType: 'SELECT_PROCEEDING';
  proceeding: Proceeding;
}

export interface EvalCriteriaTranslationItem extends BaseEvaluationItem {
  criteriaType: 'SELECT_TRANSLATION';
  translation: Translation;
}

export interface EvalCriteriaCourseItem extends BaseEvaluationItem {
  criteriaType: 'SELECT_COURSE';
  course: Course;
}

export interface EvalCriteriaCommitteeItem extends BaseEvaluationItem {
  criteriaType: 'SELECT_COMMITTEE';
  committee: Committee;
}

export interface EvalCriteriaProjectItem extends BaseEvaluationItem {
  criteriaType: 'SELECT_PROJECT';
  project: Project;
}

export interface EvalCriteriaPatentItem extends BaseEvaluationItem {
  criteriaType: 'SELECT_PATENT';
  patent: Patent;
}

export interface EvaluationItemRequest {
  criteriaType: EvalTemplateCriteriaType;
}
